import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Avatar, Chip, Menu, MenuItem } from "@mui/material";
import dayjs from "dayjs";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";
import BaseConfirmModal from "../../components/ui/BaseConfirmModal";
import AdminFilters from "./AdminFilters";
import ApproveCustomerModal from "./components/ApproveCustomerModal";
import { fetchCustomers } from "../../services/api/admin";
import { activateCompanyUser, deactivateCompanyUser } from "../../services/api/company";
import { showToast } from "../../store/toast/toastSlice";
import { setAdminFilters } from "../../store/filters/adminFiltersSlice";
import { renderUserCell } from "../../components/ui/UserProfileLink";
import { debounce, formatPhoneNumber } from "../../utils/helpers";
import { INCOMPLETE_CHIP_COLOR } from "../../utils/variables";
import BaseWrapper from "../../components/ui/BaseWrapper";

const menuOptions = [
  { label: "View Profile", value: "VIEW", icon: <i className="fa-solid fa-eye" /> },
  // Approving a submitted profile is what lets a self-registered customer into the
  // portal — it opens the modal that assigns the branch and teams first.
  { label: "Approve", value: "APPROVE", icon: <i className="fa-solid fa-square-check" /> },
  { label: "Activate", value: "ACTIVATE", icon: <i className="fa-solid fa-toggle-on" /> },
  { label: "Deactivate", value: "DEACTIVATE", icon: <i className="fa-solid fa-toggle-off" /> },
];

const workOrderStatusColumns = [
  { field: "publishedOrRouted", headerName: "Published / Routed", status: "PUBLISHED", flex: 1.2 },
  { field: "assigned", headerName: "Assigned", status: "ASSIGNED", flex: 0.9 },
  { field: "workDone", headerName: "Work Done", status: "WORKDONE", flex: 0.9 },
  { field: "approved", headerName: "Approved", status: "APPROVED", flex: 0.9 },
  { field: "paid", headerName: "Paid", status: "PAID", flex: 0.7 },
].map(({ field, headerName, status, flex }) => ({
  field,
  headerName,
  flex,
  minWidth: headerName.length > 9 ? 140 : 100,
  sortable: false,
  renderCell: (params) => (
    <p className="text-primary">{params.row.workOrdersByStatus?.[status] || 0}</p>
  ),
}));

const CustomersTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [sortModel, setSortModel] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isConfirmActivateOpen, setIsConfirmActivateOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const customerFilters = useSelector((state) => state.adminFilters.customer);

  const filtersPayload = useMemo(
    () => ({
      customerName: customerFilters.customerName || undefined,
      email: customerFilters.email || undefined,
      country: customerFilters.country?.label || undefined,
      city: customerFilters.city?.label || undefined,
      status: customerFilters.status || undefined,
    }),
    [customerFilters],
  );

  const queryParams = {
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    search,
    sortBy: sortModel[0]?.field,
    sortOrder: sortModel[0]?.sort,
  };

  const { data, isPending, refetch } = useQuery({
    queryKey: ["adminCustomers", queryParams, filtersPayload],
    queryFn: () => fetchCustomers({ ...queryParams, ...filtersPayload }),
    placeholderData: (prev) => prev,
  });

  const appliedFilters = useMemo(() => {
    const applied = [];
    if (customerFilters.customerName)
      applied.push({
        prefix: "Customer",
        value: customerFilters.customerName,
        key: "customerName",
      });
    if (customerFilters.email)
      applied.push({ prefix: "Email", value: customerFilters.email, key: "email" });
    if (customerFilters.country?.label)
      applied.push({ prefix: "Country", value: customerFilters.country.label, key: "country" });
    if (customerFilters.city?.label)
      applied.push({ prefix: "City", value: customerFilters.city.label, key: "city" });
    if (customerFilters.status) {
      const statusLabelMap = {
        PENDING: "Pending",
        SUBMITTED: "Submitted",
        APPROVED: "Active",
        ACTIVATED: "Active",
        DEACTIVATED: "In-Active",
      };
      applied.push({
        prefix: "Status",
        value: statusLabelMap[customerFilters.status] || customerFilters.status,
        key: "status",
      });
    }
    return applied;
  }, [customerFilters]);

  const handleRemoveFilter = (key) => {
    const emptyValue = ["country", "city"].includes(key) ? { value: "", label: "" } : "";
    dispatch(setAdminFilters({ module: "customer", filterName: key, data: emptyValue }));
    if (key === "country") {
      dispatch(
        setAdminFilters({ module: "customer", filterName: "city", data: { value: "", label: "" } }),
      );
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const { mutateAsync: activateUser, isPending: isActivating } = useMutation({
    mutationFn: activateCompanyUser,
    onSuccess: () => {
      dispatch(showToast({ message: "Customer activated successfully", type: "success" }));
      refetch();
      setIsConfirmActivateOpen(false);
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to activate customer",
          type: "error",
        }),
      );
    },
  });

  const { mutateAsync: deactivateUser, isPending: isDeactivating } = useMutation({
    mutationFn: deactivateCompanyUser,
    onSuccess: () => {
      dispatch(showToast({ message: "Customer deactivated successfully", type: "success" }));
      refetch();
      setIsConfirmDeactivateOpen(false);
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to deactivate customer",
          type: "error",
        }),
      );
    },
  });

  const handleItemClick = (item, row) => {
    setSelectedCustomer(row);
    if (item === "VIEW") {
      navigate(`/admin/review/customers/${row.id}?tab=profile`);
    } else if (item === "APPROVE") {
      setIsApproveOpen(true);
    } else if (item === "ACTIVATE") {
      setIsConfirmActivateOpen(true);
    } else if (item === "DEACTIVATE") {
      setIsConfirmDeactivateOpen(true);
    }
  };

  const customers = data?.customers || [];
  const rowCount = data?.totalCount || 0;

  const debouncedSearchRef = useRef(
    debounce((value) => {
      setSearch(value.trim());
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500),
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedSearchRef.current(e.target.value);
  };

  const handleContextMenu = (event, row) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null,
    );
    setSelectedCustomer(row);
  };

  const handleClose = () => {
    setContextMenu(null);
    setSelectedCustomer(null);
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params.row)}
          className="text-primary cursor-pointer"
        >
          {params?.value}
        </p>
      ),
    },
    {
      field: "clientName",
      headerName: "Full Name",
      flex: 1.4,
      minWidth: 160,
      renderCell: (params) => (
        <div
          onContextMenu={(e) => handleContextMenu(e, params.row)}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Avatar
            src={params.row?.clientLogo?.url || "/default-avatar.png"}
            alt={params.row.clientName}
            sx={{ width: 32, height: 32 }}
          />
          <p className="text-primary cursor-pointer">{params.row.clientName}</p>
        </div>
      ),
    },
    {
      field: "userCount",
      headerName: "Users",
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => <p className="text-primary">{params?.value ?? 0}</p>,
    },
    {
      field: "entityCount",
      headerName: "Entities",
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => <p className="text-primary">{params?.value ?? 0}</p>,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => <p>{params.row.owner?.email || params.row.email || "-"}</p>,
    },
    {
      field: "phoneNumber",
      headerName: "Mobile",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <p>{formatPhoneNumber(params?.value) || "-"}</p>,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1.6,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => <p>{params.row.address?.addressLine1 || "-"}</p>,
    },
    {
      field: "city",
      headerName: "City",
      flex: 1,
      minWidth: 110,
      renderCell: (params) => <p>{params.row.address?.city || "-"}</p>,
    },
    {
      field: "state",
      headerName: "State",
      flex: 1,
      minWidth: 110,
      renderCell: (params) => <p>{params.row.address?.state || "-"}</p>,
    },
    {
      field: "zipCode",
      headerName: "Zip / Post Code",
      flex: 0.9,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <p>{params.row.address?.zipCode || "-"}</p>,
    },
    {
      field: "country",
      headerName: "Country",
      flex: 0.9,
      minWidth: 100,
      renderCell: (params) => <p>{params.row.address?.country || "-"}</p>,
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1.2,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        // Rendered as a chip, not a name: "Self" sitting in a column of people's
        // names read like someone was called Self.
        if (params.row?.isSelfSignup) {
          return (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
              Self Signup
            </span>
          );
        }
        return renderUserCell(params.row?.createdBy);
      },
    },
    {
      field: "createdAt",
      headerName: "Date of Creation",
      flex: 1.3,
      minWidth: 160,
      renderCell: (params) => <p>{dayjs(params.value).format("YYYY/MM/DD hh:mm A")}</p>,
    },
    {
      field: "projectCount",
      headerName: "Projects",
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => <p className="text-primary">{params?.value ?? 0}</p>,
    },
    {
      field: "totalWorkOrders",
      headerName: "Total Work Orders",
      flex: 1.2,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => <p className="text-primary">{params?.value ?? 0}</p>,
    },
    ...workOrderStatusColumns,
    {
      field: "status",
      headerName: "Status",
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => {
        const status = params.row.owner?.status;
        if (status === "APPROVED" || status === "ACTIVATED")
          return (
            <Chip label="Active" color="success" size="small" className="w-24 justify-center" />
          );
        if (status === "DEACTIVATED")
          return (
            <Chip label="In-Active" color="error" size="small" className="w-24 justify-center" />
          );
        if (status === "SUBMITTED")
          return (
            <Chip
              label="Submitted"
              color="secondary"
              size="small"
              className="w-24 justify-center"
            />
          );
        if (status === "UNAPPROVE")
          return (
            <Chip
              label="Incomplete"
              size="small"
              className="w-24 justify-center"
              sx={{ backgroundColor: INCOMPLETE_CHIP_COLOR, color: "#fff" }}
            />
          );
        return (
          <Chip
            label="Pending"
            color="primary"
            size="small"
            variant="outlined"
            className="w-24 justify-center"
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 90,
      sortable: false,
      renderCell: (params) => {
        const status = params.row.owner?.status;
        const filteredMenuOptions = menuOptions.filter((item) => {
          // "Approve" for an account still waiting on review, "Activate" for one
          // that was switched off — the same action, named for the situation.
          const awaitingApproval = ["PENDING", "SUBMITTED"].includes(status);
          if (item.value === "APPROVE" && !awaitingApproval) return false;
          if (
            item.value === "ACTIVATE" &&
            (awaitingApproval || ["APPROVED", "ACTIVATED"].includes(status))
          )
            return false;
          if (item.value === "DEACTIVATE" && status === "DEACTIVATED") return false;
          return true;
        });
        return (
          <BaseDropdownV2
            menuOptions={filteredMenuOptions}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(item) => handleItemClick(item, params.row)}
          />
        );
      },
    },
  ];

  const handleCellClick = (params) => {
    if (params.field === "id" || params.field === "clientName") {
      navigate(`/admin/review/customers/${params.row.id}`);
    }
  };

  return (
    <>
      <div className="border-b border-zinc-300 bg-white">
        <BaseWrapper>
          <div className="grid grid-cols-7 gap-y-3 py-[7px] items-center">
            <div className="md:col-span-3 col-span-7">
              <h3 className=" md:text-left text-center">Customers</h3>
            </div>
          </div>
        </BaseWrapper>
      </div>
      <BaseWrapper>
        <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
            <div
              className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5 cursor-pointer"
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
                <i className="fa-light fa-filter text-white text-sm"></i>
              </span>
              <p className="text-primary ml-2 ">Add Filter</p>
            </div>
            <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
              <div className="flex gap-2">
                {appliedFilters.map((f) => (
                  <div
                    key={f.key}
                    className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                  >
                    <span className="text-primary">{f.prefix}:</span>&nbsp;
                    <p className="text-primary text-sm mx-1">{f.value}</p>
                    <button
                      onClick={() => handleRemoveFilter(f.key)}
                      className="ml-2 text-primary hover:text-red-500"
                      aria-label={`Remove ${f.prefix} ${f.value}`}
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full sm:w-[28%]">
              <BaseTextField
                id="customersSearch"
                name="customersSearch"
                type="search"
                autoComplete="off"
                placeholder="Search by name, email or ID"
                value={searchQuery}
                onChange={handleSearchChange}
                startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
              />
            </div>
          </div>
        </div>

        <BaseDataTableV2
          className="max-h-[calc(100vh-320px)]"
          rowHeight={38}
          columns={columns}
          data={customers}
          isLoading={isPending}
          onCellClick={handleCellClick}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          hideFooter={false}
          disableColumnSorting={false}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        />

        {isFilterDrawerOpen && (
          <AdminFilters
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            fetchData={refetch}
            isLoading={isPending}
            setFiltersChanged={() => {}}
            entity="customer"
          />
        )}

        {isApproveOpen && (
          <ApproveCustomerModal
            open={isApproveOpen}
            onClose={() => setIsApproveOpen(false)}
            customer={selectedCustomer}
            onApproved={refetch}
          />
        )}

        {isConfirmActivateOpen && (
          <BaseConfirmModal
            open={isConfirmActivateOpen}
            onClose={() => setIsConfirmActivateOpen(false)}
            onConfirm={() => activateUser(String(selectedCustomer?.owner?.id))}
            isLoading={isActivating}
            title="Are you sure you want to activate this customer?"
            description="Once activated, the customer will regain access and its status will be set to Active."
            confirmText="Activate"
            confirmButtonColor="primary"
          />
        )}

        {isConfirmDeactivateOpen && (
          <BaseConfirmModal
            open={isConfirmDeactivateOpen}
            onClose={() => setIsConfirmDeactivateOpen(false)}
            onConfirm={() => deactivateUser(String(selectedCustomer?.owner?.id))}
            isLoading={isDeactivating}
            title="Are you sure you want to deactivate this customer?"
            description="Once deactivated, the customer will lose access and its status will be set to Inactive."
            confirmText="Deactivate"
            confirmButtonColor="error"
          />
        )}

        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
          }
        >
          <MenuItem
            onClick={() => {
              navigate(`/admin/review/customers/${selectedCustomer?.id}`);
              handleClose();
            }}
          >
            <p>Open</p>
          </MenuItem>
          <MenuItem
            onClick={() => {
              window.open(`/admin/review/customers/${selectedCustomer?.id}`, "_blank");
              handleClose();
            }}
          >
            <p>Open in New Tab</p>
          </MenuItem>
        </Menu>
      </BaseWrapper>
    </>
  );
};

export default CustomersTable;
